/**
 * SupervisoryVisitDialog - Record Supervisory Visit Dialog Component
 * Matches the exact implementation from ClientDetailPage
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function SupervisoryVisitDialog({
  open,
  onOpenChange,
  newSupervisoryVisit,
  setNewSupervisoryVisit,
  supervisoryFormFile,
  setSupervisoryFormFile,
  handleVisitDateChange,
  userName,
  saving,
  onSave,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Supervisory Visit</DialogTitle>
          <DialogDescription>
            Document a supervisory visit and schedule the next one
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Visit Date *</Label>
              <Input
                type="date"
                value={newSupervisoryVisit.visit_date}
                onChange={(e) => handleVisitDateChange(e.target.value)}
                data-testid="supervisory-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={newSupervisoryVisit.visit_completed ? "completed" : "pending"}
                onValueChange={(val) => setNewSupervisoryVisit(prev => ({ ...prev, visit_completed: val === "completed" }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Supervisor Name</Label>
            <Input
              value={newSupervisoryVisit.supervisor_name}
              onChange={(e) => setNewSupervisoryVisit(prev => ({ ...prev, supervisor_name: e.target.value }))}
              placeholder={userName || "Enter name"}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Visit Notes</Label>
            <Textarea
              value={newSupervisoryVisit.notes}
              onChange={(e) => setNewSupervisoryVisit(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Notes from the supervisory visit..."
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Upload Visit Form (optional)</Label>
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.png"
              onChange={(e) => setSupervisoryFormFile(e.target.files?.[0] || null)}
              data-testid="supervisory-form-upload"
            />
            {supervisoryFormFile && (
              <p className="text-xs text-slate-500">Selected: {supervisoryFormFile.name}</p>
            )}
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg">
            <Label className="text-blue-800 font-medium">Schedule Next Visit</Label>
            <p className="text-xs text-blue-600 mb-3">
              Auto-set to 55 days after visit date. This will add the next visit to the calendar.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Next Visit Date</Label>
                <Input
                  type="date"
                  value={newSupervisoryVisit.next_visit_date}
                  onChange={(e) => setNewSupervisoryVisit(prev => ({ ...prev, next_visit_date: e.target.value }))}
                  data-testid="supervisory-next-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Time</Label>
                <Input
                  type="time"
                  value={newSupervisoryVisit.next_visit_time}
                  onChange={(e) => setNewSupervisoryVisit(prev => ({ ...prev, next_visit_time: e.target.value }))}
                  data-testid="supervisory-next-time"
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save Visit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
